import 'regenerator-runtime/runtime';
import { call, put, fork, all, select, takeEvery } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { parseError } from 'logic/helpers';
import * as constants from 'constants/index';
import * as actions from 'actions/index';
import { fetchConversationSuccess } from 'actions/conversation';
import api from '../api';

function* fetchBookingsInfoRequest(req) {
  try {
    const { conversationId } = req.payload;
    const [conversation, cottages] = yield all([
      call(api.fetchConversation.bind(null, conversationId)),
      call(api.fetchRentableCottages.bind(null)),
    ]);
    yield [
      put(fetchConversationSuccess(conversation)),
      put(actions.fetchBookingsInfoSuccess({ cottages })),
    ];
    return;
  } catch (e) {
    if (e.status === 401) {
      yield [put(actions.postLogoutRequest()), put(actions.fetchBookingsFailure({ error: {} }))];
    } else {
      yield put(
        actions.fetchBookingsFailure({
          error: {
            status: e.status,
            message: e.message || 'Could not load bookings.',
            canTryAgain: true,
          },
        }),
      );
    }
  }
}

function* fetchBookingsRequest(req) {
  const maxTryAgain = 3;
  for (let i = 1; i <= maxTryAgain; i++) {
    try {
      const { conversationId } = req.payload;
      const data = yield call(api.fetchBookings.bind(null, conversationId));
      yield put(actions.fetchBookingsSuccess({ data }));
      return;
    } catch (e) {
      if (i === maxTryAgain || e.status === 401) {
        if (e.status === 401) {
          yield [
            put(actions.postLogoutRequest()),
            put(actions.fetchBookingsFailure({ error: {} })),
          ];
        } else {
          yield put(
            actions.fetchBookingsFailure({
              error: {
                status: e.status,
                message: e.message || 'Could not load bookings.',
                canTryAgain: true,
              },
            }),
          );
        }
        return;
      }
      yield call(delay, 2000);
    }
  }
}

function* handleStaleConflict(bookingId, e) {
  if (e.data?.booking) {
    yield put(actions.fetchBookingSuccess({ data: e.data.booking }));
  } else {
    yield put(actions.fetchBookingRequest({ bookingId }));
  }
  // setOfferStale reducer already clears isPosting; no failure dispatch so
  // the friendly inline banner is the only signal (no duplicate snackbar).
  yield put(actions.bookingOfferStaleSet());
}

export function* fetchBookingRequest(req) {
  try {
    const { bookingId } = req.payload;
    const data = yield call(api.fetchBooking.bind(null, bookingId));
    yield put(actions.fetchBookingSuccess({ data }));
    return;
  } catch (e) {
    if (e.status === 401) {
      yield [put(actions.postLogoutRequest()), put(actions.fetchBookingFailure({ error: {} }))];
    } else {
      yield put(
        actions.fetchBookingFailure({
          error: {
            status: e.status,
            message: e.message || 'Could not load bookings.',
            canTryAgain: true,
          },
        }),
      );
    }
  }
}

function* fetchCancellationPolicyRequest() {
  const maxTryAgain = 3;
  for (let i = 1; i <= maxTryAgain; i++) {
    try {
      const cancellationPolicy = yield call(api.fetchCancellationPolicy.bind(null));
      yield put(actions.fetchCancellationPolicySuccess({ cancellationPolicy }));
      return;
    } catch (e) {
      yield put(actions.fetchCancellationPolicyFailure(parseError(e)));
    }
  }
}

export function* postBookingRequest(req) {
  try {
    const bookingActive = yield select((state) => state.user.data.booking_active);

    const data = yield call(api.postBooking.bind(null, req.payload));
    yield put(actions.postBookingSuccess(data));

    // if the user didnt had mango user, fetch again to retrieve updated from api
    if (!bookingActive) {
      yield put(actions.fetchUserSelfRequest());
    }

    // Take the conversation from the payload the booking was created with
    // (Create.jsx sources it from the route, and cleanUpData preserves it)
    // rather than from the store. Create re-establishes state.conversation.id
    // on mount today, but the conversations list now clears that pointer, so
    // the redirect should not depend on it.
    const conversationId = req?.payload?.conversation_id;
    const bookingId = yield select((state) => state.bookings.singleBooking.id);
    if (conversationId && bookingId) {
      window.location.replace(`/dashboard/conversations/${conversationId}/bookings/${bookingId}`);
    }
  } catch (e) {
    yield put(actions.postBookingFailure(parseError(e)));
  }
}

export function* putBookingRequest(req) {
  let bookingId;
  try {
    const { bookingId: id, values } = req.payload;
    bookingId = id;
    const data = yield call(api.putBooking.bind(null, bookingId, values));
    yield put(actions.putBookingSuccess(data));
  } catch (e) {
    if ((e.status === 409 || e.status === 423) && bookingId) {
      yield* handleStaleConflict(bookingId, e);
      return;
    }
    yield put(actions.putBookingFailure(parseError(e)));
  }
}

export function* bookingActionRequest(req) {
  let bookingId;
  try {
    const {
      bookingId: id,
      action,
      cancellation_reason,
      payment_method,
      card,
      klarna_addressline1,
      klarna_city,
      klarna_postalcode,
      version,
    } = req.payload;
    bookingId = id;
    if (action === 'cancel') {
      const data = yield call(
        api.bookingAction.bind(
          null,
          bookingId,
          action,
          cancellation_reason,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          version,
        ),
      );
      yield put(actions.bookingActionSuccess(data));
    } else {
      const bookingActive = yield select((state) => state.user.data.booking_active);

      const data = yield call(
        api.bookingAction.bind(
          null,
          bookingId,
          action,
          cancellation_reason,
          payment_method,
          card,
          klarna_addressline1,
          klarna_city,
          klarna_postalcode,
          version,
        ),
      );

      if (!bookingActive) {
        yield put(actions.fetchUserSelfRequest());
      }

      window.location.href = data.RedirectURL;
      // yield put(actions.bookingActionSuccess(data));
    }
  } catch (e) {
    if ((e.status === 409 || e.status === 423) && bookingId) {
      yield* handleStaleConflict(bookingId, e);
      return;
    }
    yield put(actions.bookingActionFailure(parseError(e)));
  }
}

export default [
  fork(takeEvery, constants.FETCH_BOOKINGS_INFO_REQUEST, fetchBookingsInfoRequest),
  fork(takeEvery, constants.FETCH_BOOKINGS_REQUEST, fetchBookingsRequest),
  fork(takeEvery, constants.FETCH_BOOKING_REQUEST, fetchBookingRequest),
  fork(takeEvery, constants.FETCH_CANCELLATION_POLICY_REQUEST, fetchCancellationPolicyRequest),
  fork(takeEvery, constants.POST_BOOKING_REQUEST, postBookingRequest),
  fork(takeEvery, constants.BOOKING_ACTION_REQUEST, bookingActionRequest),
  fork(takeEvery, constants.PUT_BOOKING_REQUEST, putBookingRequest),
];
